import * as CL from '@design-system/component-library';
import * as React from 'react';
import { type JSX, useState } from 'react';
import { useNavigate } from 'react-router';
import { paths } from '../../common/constants/pathVariables';
import { createVirtualCatalog } from '../../common/fetch';
import { somethingWentWrongMsg, t } from '../../common/i18n/index.js';
import { Catalog, EppSolutionResponse } from '../../generated/api/models.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { DialogWrapper } from '../DialogWrapper/index.js';
import { Loading } from '../Loading/Loading.js';
import type { SecondaryAccount } from '../../generated/api/models.js';

import './CopyCatalogMultiBusinessDialog.scss';

interface AccountData {
  accountMasterId: string;
  accountName: string;
}

interface CopyCatalogMultiBusinessDialogProps {
  accounts: AccountData[];
  catalog: Catalog;
  isSubmitInProgress?: boolean;
  secondaryAccountsInfo?: SecondaryAccount[];
}

const getNotAllowedAccountsToCopyCatalogTo = (secondaryAccountInfo: SecondaryAccount[]): string[] => {
  return secondaryAccountInfo
    .filter(acc => acc.eppSolution?.eppSolutionStatus !== EppSolutionResponse.EppSolutionStatusEnum.ACTIVE)
    .map(acc => acc.accountMasterId);
};

const getNotAllowedAccounts = (productType?: Catalog.ProductTypeEnum, secondaryAccountInfo?: SecondaryAccount[]) => {
  if (productType === Catalog.ProductTypeEnum.EPP_RECURRING) {
    const notAllowedAccounts = getNotAllowedAccountsToCopyCatalogTo(secondaryAccountInfo || []);
    return {
      notAllowedAccounts,
      notAllowedToCopyMessage: notAllowedAccounts.length
        ? t.SIEL(
            'Customers listed as unavailable do not have existing EPP contracts. Please contact your designated Elisa contact person for creating a new EPP contract.'
          )
        : undefined,
    };
  }
  return {
    notAllowedAccounts: undefined,
    notAllowedToCopyMessage: undefined,
  };
};

const SelectionBoxes = ({
  availableAccounts,
  selectedAccounts,
  notAllowedAccounts,
  onChange,
}: {
  availableAccounts: AccountData[];
  selectedAccounts: Set<string>;
  notAllowedAccounts?: string[];
  onChange: (accountMasterId: string, checked: boolean) => void;
}): JSX.Element => {
  return (
    <>
      {availableAccounts.map((account, index) => {
        return (
          <CL.Checkbox
            id={account.accountMasterId || `${index}`}
            key={`${account.accountMasterId}_${selectedAccounts.has(account.accountMasterId)}`}
            disabled={(notAllowedAccounts?.length && notAllowedAccounts.includes(account.accountMasterId)) || false}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              onChange(account.accountMasterId, e.currentTarget.checked);
            }}
            checked={selectedAccounts.has(account.accountMasterId)}
          >
            {account.accountName || ''}
          </CL.Checkbox>
        );
      })}
    </>
  );
};

const MultipleAccountsDialogContent = ({
  availableAccounts,
  onChange,
  selectedAccounts,
  notAllowedAccounts,
  notAllowedToCopyMessage,
  validationError,
}: {
  availableAccounts: AccountData[];
  onChange: (accountMasterId: string, checked: boolean) => void;
  selectedAccounts: Set<string>;
  notAllowedAccounts?: string[];
  notAllowedToCopyMessage?: string;
  validationError?: string;
}) => {
  return (
    <>
      <div>
        {t.AMXZ(
          'Select the companies to which you want to copy the device list. Be sure to finalize and publish the device list separately.'
        )}
      </div>
      <SelectionBoxes {...{ availableAccounts, selectedAccounts, notAllowedAccounts, onChange }} />
      {validationError && <div className="ea-text--error">{validationError}</div>}
      <div className="ea-disclaimertext">{notAllowedToCopyMessage}</div>
      <div className="ea-disclaimertext">
        {t.UQMA(
          `You can find the companies' own device lists on their own device list page. Copying does not overwrite existing lists, so delete old lists if necessary.`
        )}
      </div>
    </>
  );
};

const getInitiallySelectedAccounts = (accounts: AccountData[]): Set<string> => {
  if (accounts.length === 1) {
    return new Set([accounts[0].accountMasterId]);
  }
  return new Set();
};

export const CopyCatalogMultiBusinessDialog = (props: CopyCatalogMultiBusinessDialogProps) => {
  const navigate = useNavigate();
  const { catalog, accounts, isSubmitInProgress, secondaryAccountsInfo } = props;
  const [selectedAccounts, setSelectedAccounts] = useState(() => getInitiallySelectedAccounts(accounts));
  const [error, setError] = useState<string>();

  const onCloseDialog = () => navigate(location.search);

  const onSubmit = async () => {
    if (!selectedAccounts.size) {
      setError(t.WPOQ('Please select a company to proceed'));
    } else {
      const proms = Array.from(selectedAccounts).map(e => createVirtualCatalog(catalog, e));
      const results = await Promise.allSettled(proms);
      const allSucceeded = results.every(r => r.status === 'fulfilled');
      const allFailed = results.every(r => r.status === 'rejected');

      if (allSucceeded) {
        addNotification(
          t.I9RX(`Device lists copied. Complete and publish the lists on the companies' own device list page.`)
        );
        navigate(paths.COMPANY_INFO_CATALOGS);
      } else {
        const allFailedMsg = t.JDTO(somethingWentWrongMsg);
        const someFailedMsg = t.UZ2G(
          'Selected catalog cannot be copied to all of the selected companies. Please try again.'
        );
        setError(allFailed ? allFailedMsg : someFailedMsg);
      }
    }
  };

  const { notAllowedAccounts, notAllowedToCopyMessage } = getNotAllowedAccounts(
    catalog.productType,
    secondaryAccountsInfo
  );

  const showLoading = catalog.productType === Catalog.ProductTypeEnum.EPP_RECURRING && !secondaryAccountsInfo;

  return (
    <DialogWrapper
      buttons={[
        {
          color: 'link',
          text: t.B2V1('Cancel'),
          onClick: onCloseDialog,
        },
        {
          text: t.UEOZ('Copy'),
          onClick: onSubmit,
          disabled: !accounts.length || (accounts.length > 1 && showLoading),
          isLoading: isSubmitInProgress,
        },
      ]}
      closeable
      header={t.H2ZI('Create a copy of the device list')}
      onCloseDialog={onCloseDialog}
    >
      <div className="of-copy-catalog-multi-business-dialog__content">
        {accounts.length > 1 &&
          (showLoading ? (
            <Loading />
          ) : (
            <MultipleAccountsDialogContent
              {...{
                availableAccounts: accounts,
                onChange: (accountMasterId, checked) => {
                  if (checked) {
                    setSelectedAccounts(prev => new Set(prev).add(accountMasterId));
                  } else {
                    setSelectedAccounts(prev => {
                      const next = new Set(prev);
                      next.delete(accountMasterId);
                      return next;
                    });
                  }
                  setError(undefined);
                },
                selectedAccounts,
                notAllowedAccounts,
                notAllowedToCopyMessage,
                validationError: error,
              }}
            />
          ))}
        {accounts.length === 1 && t.KC4V('Are you sure you want to proceed?')}
        {!accounts.length && <Loading />}
      </div>
    </DialogWrapper>
  );
};
